import { useState, useEffect } from 'react';
import { debounce } from 'lodash';

import { breakpoints } from 'styles/theme/breakpoints';
import { useIsMounted } from './useIsMounted';

export function useWindowSize() {
  // Initialize state with undefined width/height so server and client renders match
  // Learn more here: https://joshwcomeau.com/react/the-perils-of-rehydration/
  const [windowSize, setWindowSize] = useState({
    width: undefined,
    height: undefined,
  });

  /**
   * visualViewport is what portion of the screen is visible when the keyboard or any other OS related item is visible
   * https://developer.mozilla.org/en-US/docs/Web/API/VisualViewport
   */
  const [visualViewportSize, setVisualViewportSize] = useState({
    height: undefined,
    width: undefined,
  });

  const [screenOrientation, setScreenOrientation] = useState('portrait');
  const isMounted = useIsMounted();

  const isDesktop = windowSize.width >= breakpoints.mediumLarge;
  const isMobile = windowSize.width < breakpoints.mediumSmall;
  const isTablet =
    breakpoints.mediumSmall <= windowSize.width && windowSize.width < breakpoints.mediumLarge;

  const isMobileOrTablet = isMobile || isTablet;

  useEffect(() => {
    const handleResize = debounce(
      () => {
        if (isMounted()) {
          setWindowSize({
            width: document.documentElement.clientWidth || window.innerWidth,
            height: document.documentElement.clientHeight || window.innerHeight,
          });
          const orientation = window.innerHeight / window.innerWidth > 1 ? 'portait' : 'landscape';
          setScreenOrientation(orientation);
        }
      },
      500,
      { leading: !isMobileOrTablet, trailing: true },
    );

    const handleVisualViewportResize = debounce((e = null) => {
      if (isMounted()) {
        const viewport = e?.target;
        setVisualViewportSize({
          height: viewport?.height || window?.visualViewport?.height || window?.innerHeight,
          width: viewport?.width || window?.visualViewport?.width || window?.innerWidth,
        });
      }
    }, 500);

    handleResize();
    handleVisualViewportResize();

    // only execute all the code below in client side
    if (typeof window !== 'undefined') {
      // Add event listener
      window.addEventListener('resize', handleResize);
      window?.visualViewport?.addEventListener('resize', handleVisualViewportResize);
    }
    // Remove event listener on cleanup
    return () => {
      void window.removeEventListener('resize', handleResize);
      window?.visualViewport?.removeEventListener('resize', handleVisualViewportResize);
    };
  }, []);

  return {
    isDesktop,
    isMobile,
    isMobileOrTablet,
    isTablet,
    screenOrientation,
    visualViewportSize,
    windowSize,
  };
}
